import TruCard from '../../../components/TruCard';
import * as theme from '../../../theme';
import { formatCentsToDollars } from '../../../contexts/CostContext';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import PaymentIcon from '@mui/icons-material/Payment';
import dayjs from 'dayjs';
import { Button } from '@mui/material';
import logo from '../../../images/tb-logo-dark.png';
import icon from '../../../images/tb-icon.png';
import { cardTypes } from '../../../contexts/TransactionTypeContext';
import { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../../firebase';

export default function TotalPaid({ scan, client, practitioner }) {
  const [pracTransactions, setPracTransactions] = useState([]);

  useEffect(() => {
    if (scan && scan.transactions && client && client.practitionerId) {
      const fetchPracTrans = async () => {
        const years = [...new Set(scan.transactions.map(t => dayjs(t.date.toDate()).year().toString()))];
        const allTrans = [];
        for (const year of years) {
          const docRef = doc(db, `practitioners/${client.practitionerId}/transactions`, year);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            allTrans.push(...(docSnap.data().trans || []));
          }
        }
        setPracTransactions(allTrans);
      };
      fetchPracTrans();
    }
  }, [scan, client]);

  const getCardDetails = (transId) => {
    const matching = pracTransactions.find(pt => pt.id === transId);
    return matching ? matching.card : null;
  };

  const getTransactionDescription = (type) => {
    switch (type) {
      case 'scan-down':
        return 'Scan Initial';
      case 'balance':
        return 'Scan Remainder';
      case 'extra-imprint':
        return 'Additional Imprint Remedy';
      case 'scan-down-refund':
        return 'Scan Refund';
      default:
        return type.charAt(0).toUpperCase() + type.slice(1);
    }
  };

  const isRefund = (type) => type.includes('refund');

  const getTransactionAmount = (t) => {
    return formatCentsToDollars(t.amount);
  };

  const totalPaid = scan.transactions
    ? scan.transactions.reduce((sum, t) => sum + t.amount, 0)
    : 0;

  const getCard = (brand) => {
    if (brand !== '') {
      return cardTypes[brand] || 'CARD';
    } else {
      return 'CARD';
    }
  };

  const handleViewReceipt = () => {
    const practitionerName = practitioner.businessName 
      ? `${practitioner.firstName} ${practitioner.lastName} (${practitioner.businessName})`
      : `${practitioner.firstName} ${practitioner.lastName}`;

    const clientName = `${client.firstName} ${client.lastName}`;
    const scanDate = dayjs(scan.scanDate).format('MMMM D, YYYY');

    let itemsHtml = '';
    let totalAmount = 0;

    scan.transactions.forEach((t, index) => {
      let amount = t.amount / 100;
      totalAmount += amount;

      const formattedDate = dayjs(t.date.toDate()).format('MMMM D, YYYY [at] h:mm A');
      const card = getCardDetails(t.transId);
      const paymentMethod = card ? `${getCard(card.brand)} x${card.last4 || '0000'}` : 'N/A';

      let description = getTransactionDescription(t.type);

      itemsHtml += `
        <tr>
          <td style="padding: 8px; border-bottom: 1px solid #ddd;">${description}</td>
          <td style="padding: 8px; border-bottom: 1px solid #ddd;">${formattedDate}</td>
          <td style="padding: 8px; border-bottom: 1px solid #ddd;">${paymentMethod}</td>
          <td style="padding: 8px; border-bottom: 1px solid #ddd; text-align: right;">$${amount.toFixed(2)}</td>        </tr>
      `;
    });

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>TruBalance Receipt</title>
        <style>
          body { font-family: Arial, sans-serif; background: white; margin: 20px; }
          .container { max-width: 800px; margin: 0 auto; }
          .header { text-align: center; }
          .logo { width: 300px; }
          .icon { width: 70px; margin-top: 40px; }
          .business-info { margin-top: 10px; font-size: 14px; }
          .billed-to { text-align: center; margin-top: 20px; font-size: 14px; }
          .business-name { font-weight: bold; font-size: 16px; margin-bottom: 6px }
          .details { margin-top: 20px; }
          .table { width: 100%; border-collapse: collapse; margin-top: 30px; }
          .table th { background: #f4f4f4; padding: 8px; text-align: left; }
          .table td { padding: 8px; }
          .total { text-align: right; font-weight: bold; padding-right: 8px; margin-top: 30px; font-size: 18px; }
          .print-btn { position: absolute; top: 20px; right: 20px; padding: 8px 16px; background: ${theme.Colors.Purple}; border-radius: 4px; color: white; border: none; cursor: pointer; }
          .print-btn:hover { background: ${theme.Colors.GoldDark}; }
          @media print { .print-btn { display: none; } body { margin: 0; } }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="header">
            <img src="${logo}" alt="TruBalance Logo" class="logo">
            <div class="business-info">
              <div class="business-name">TruBalance Bioenergetics</div>
              10800 Old County Rd 15, Plymouth, MN 55441<br>
              hello@trubalancebioenergetics.com
            </div>
          </div>
          <div class="billed-to">
            <div class="business-name">Billed To</div>
            ${practitionerName}
          </div>
          <div class="billed-to">
            <div class="business-name">Receipt for ${clientName}'s Scan on ${scanDate}</div>
          </div>
          <table class="table">
            <thead>
              <tr>
                <th>Description</th>
                <th>Date</th>
                <th>Card</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              ${itemsHtml}
            </tbody>
          </table>
          <div class="total">
            Total Paid: $${totalAmount.toFixed(2)}
          </div>
          <div class="header">
            <img src="${icon}" alt="TruBalance Icon" class="icon">
          </div>
        </div>
        <button class="print-btn" onclick="window.print()">Print</button>
      </body>
      </html>
    `;

    const win = window.open('', '_blank');
    if (win) {
      win.document.write(html);
      win.document.close();
    } else {
      alert('Popup blocked. Please allow popups to view the receipt.');
    }
  };

  return (
    <TruCard title={'Total Paid'} styles={{ marginTop: 20 }}>
      {scan.transactions && scan.transactions.length > 0 ? (
        scan.transactions.map((t, index) => (
            <div
              key={index}
              style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '8px 0',
                borderBottom: index < scan.transactions.length - 1 ? `1px solid ${theme.Colors.GrayLight}` : 'none',
              }}
            >
              <div style={{ ...theme.Container.flexRowFixed, alignItems: 'center', flex: 1 }}>
                <PaymentIcon sx={{ flex: '0 0 auto', marginRight: '8px' }} />
                <div style={{ flex: 1 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                    {getTransactionDescription(t.type)}
                  </Typography>
                  <Typography sx={{ fontSize: '0.85em', color: theme.Colors.GrayMedium }}>
                    {dayjs(t.date.toDate()).format('MM/DD/YYYY h:mm A')}
                  </Typography>
                </div>
              </div>
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 500,
                  textAlign: 'right',
                  color: isRefund(t.type) ? theme.Colors.Red : theme.Colors.Black,
                }}
              >
                {isRefund(t.type) ? '-' : ''}${getTransactionAmount(t)}
              </Typography>
            </div>
        ))
      ) : (
        <Typography sx={{ textAlign: 'center', color: theme.Colors.GrayMedium }}>
          No transactions
        </Typography>
      )}
      <Box sx={{ textAlign: 'center', marginTop: '16px' }}>
        <Typography sx={{ fontSize: '1.5em', fontWeight: 'bold' }}>
          ${formatCentsToDollars(totalPaid)}
        </Typography>
        <Typography sx={{ fontSize: '0.8em', color: theme.Colors.GrayMedium }}>
          Total Paid
        </Typography>
      </Box>
      {scan.transactions && scan.transactions.length > 0 && (
        <Button 
          variant="text" 
          onClick={handleViewReceipt}
          sx={{ display: 'block', margin: '10px auto 0' }}
        >
          View Receipt
        </Button>
      )}
    </TruCard>
  );
}